// frontend/src/hooks/useStudentReport.ts

import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuthStore } from '../store/authStore';

export interface CourseProgress {
  courseId: string;
  title: string;
  category: string;
  progressPercent: number;
  completedLessons: number;
  totalLessons: number;
  status: string;
  lastActivityAt: string | null;
}

export interface ActivityDay {
  date: string; // YYYY-MM-DD
  lessonCount: number;
  noteCount: number;
  aiCount: number;
}

export interface ReportCritique {
  overallScore: number;   // 0-100
  tone: 'excellent' | 'good' | 'fair' | 'needs_work';
  points: { type: 'positive' | 'warning' | 'suggestion'; text: string }[];
  summary: string;
}

export interface StudentReportData {
  // Genel istatistikler
  totalLessonsCompleted: number;
  totalQuizAttempts: number;
  totalAiConversations: number;
  totalNotes: number;
  currentStreak: number;
  xp: number;
  age: number | null;

  // Kurs ilerlemesi
  courseProgress: CourseProgress[];

  // Son 30 gün aktiflik
  activeDays: ActivityDay[];
  activeDaysCount: number;  // son 30 günde kaç gün aktif

  // AI mentor sohbetleri (son 5)
  recentAiTopics: { id: string; title: string; createdAt: string }[];

  // Yapıcı eleştiri
  critique: ReportCritique;
}

function generateCritique(data: Omit<StudentReportData, 'critique'>): ReportCritique {
  const points: ReportCritique['points'] = [];

  // Aktiflik analizi
  const activeDaysCount = data.activeDaysCount;
  const lessonsPerActiveDay = activeDaysCount > 0
    ? data.totalLessonsCompleted / activeDaysCount
    : 0;

  // Streak analizi
  if (data.currentStreak >= 7) {
    points.push({ type: 'positive', text: `${data.currentStreak} günlük kesintisiz çalışma serisi harika! Bu tempo ile büyük ilerlemeler kaydediyorsun.` });
  } else if (data.currentStreak >= 3) {
    points.push({ type: 'positive', text: `${data.currentStreak} günlük streak devam ediyor. Düzenli çalışmayı sürdür!` });
  } else if (data.currentStreak === 0) {
    points.push({ type: 'warning', text: 'Bir süredır giriş yapmadın. Küçük adımlarla başlamak için bugün tek bir ders yeterli!' });
  }

  // Ders tamamlama analizi
  if (activeDaysCount >= 20) {
    points.push({ type: 'positive', text: `Son 30 günde ${activeDaysCount} gün aktif oldun — bu gerçekten etkileyici bir devamlılık!` });
  } else if (activeDaysCount >= 10) {
    points.push({ type: 'suggestion', text: `Son 30 günde ${activeDaysCount} gün çalıştın. Haftada en az 5 gün hedef koymak seni hızla ilerletir.` });
  } else if (activeDaysCount > 0) {
    points.push({ type: 'warning', text: `Son 30 günde sadece ${activeDaysCount} gün aktif oldun. Daha düzenli bir program oluşturmayı deneyelim.` });
  } else {
    points.push({ type: 'warning', text: 'Son 30 günde aktiflik kaydedilmedi. Her gün 10 dakika bile büyük fark yaratır!' });
  }

  // Kurs tamamlama derinliği
  const completedCourses = data.courseProgress.filter(c => c.status === 'completed').length;
  const inProgressCourses = data.courseProgress.filter(c => c.status === 'in_progress');
  const highProgressCourses = inProgressCourses.filter(c => c.progressPercent >= 70);

  if (completedCourses > 0) {
    points.push({ type: 'positive', text: `${completedCourses} kursu tamamen bitirmişsin — bu gerçek bir başarı!` });
  }

  if (highProgressCourses.length > 0) {
    points.push({ type: 'suggestion', text: `"${highProgressCourses[0].title}" dersinde %${highProgressCourses[0].progressPercent} ilerleme var. Bitirmek için çok az kaldı, hadi son adımı at!` });
  }

  if (inProgressCourses.length > 3) {
    points.push({ type: 'warning', text: `${inProgressCourses.length} farklı dersi aynı anda takip ediyorsun. 1-2 kursa odaklanmak daha hızlı ilerleme sağlar.` });
  }

  // AI mentor kullanımı
  if (data.totalAiConversations === 0) {
    points.push({ type: 'suggestion', text: 'AI Mentor\'u henüz kullanmadın. Anlamadığın konuları sormak öğrenme hızını 2 katına çıkarabilir!' });
  } else if (data.totalAiConversations >= 10) {
    points.push({ type: 'positive', text: `AI Mentor'u ${data.totalAiConversations} kez kullanmışsın — merak sorgulamayı bırakma!` });
  } else {
    points.push({ type: 'suggestion', text: `AI Mentor'u ${data.totalAiConversations} kez kullandın. Daha sık kullanmak anlamadığın konuları hızlıca çözmenizi sağlar.` });
  }

  // Not alma alışkanlığı
  if (data.totalNotes >= 5) {
    points.push({ type: 'positive', text: `${data.totalNotes} not almışsın — bu ileride çok işine yarayacak!` });
  } else if (data.totalNotes === 0) {
    points.push({ type: 'suggestion', text: 'Henüz not almamışsın. Ders sırasında not almak bilgiyi %40 daha kalıcı hale getirir.' });
  }

  // Genel skor hesaplama
  let score = 0;
  score += Math.min(30, activeDaysCount * 1.5);           // Aktiflik: max 30 puan
  score += Math.min(20, data.currentStreak * 2);           // Streak: max 20 puan
  score += Math.min(20, data.totalLessonsCompleted * 1.5); // Ders: max 20 puan
  score += Math.min(15, data.totalAiConversations * 3);    // AI: max 15 puan
  score += Math.min(15, data.totalNotes * 3);              // Notlar: max 15 puan
  score = Math.min(100, Math.round(score));

  let tone: ReportCritique['tone'];
  let summary: string;

  if (score >= 80) {
    tone = 'excellent';
    summary = 'Harika gidiyorsun! Öğrenme yolculuğunda çok güçlü bir başlangıç yapmışsın. Bu tempoyu korursan hedeflerine çok kısa sürede ulaşırsın.';
  } else if (score >= 60) {
    tone = 'good';
    summary = 'İyi bir ilerleme kaydediyorsun! Düzenli çalışmaya devam et ve birkaç alışkanlığını geliştirirsen çok daha hızlı ilerleyeceksin.';
  } else if (score >= 35) {
    tone = 'fair';
    summary = 'Potansiyelinin altında bir tempo görüyorum. Küçük ama düzenli adımlar atmak büyük fark yaratır — bugün tek bir ders bile yeterli!';
  } else {
    tone = 'needs_work';
    summary = 'Henüz çok az aktivite var — bu normal, başlamak her zaman zordur! Önemli olan bugünden itibaren küçük adımlarla başlamak.';
  }

  return { overallScore: score, tone, points, summary };
}

export function useStudentReport() {
  const userId = useAuthStore((s) => s.session?.user.id);
  const profile = useAuthStore((s) => s.profile);
  const [data, setData] = useState<StudentReportData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    async function load() {
      try {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const thirtyDaysAgoStr = thirtyDaysAgo.toISOString();

        const [
          completionsRes,
          quizRes,
          aiRes,
          notesCountRes,
          courseProgressRes,
          recentAiRes,
          activityRes,
        ] = await Promise.all([
          // Toplam ders tamamlama
          supabase
            .from('student_lesson_completions')
            .select('id', { count: 'exact', head: true })
            .eq('student_id', userId),
          // Quiz denemeleri
          supabase
            .from('quiz_attempts')
            .select('id', { count: 'exact', head: true })
            .eq('student_id', userId),
          // AI konuşma sayısı
          supabase
            .from('ai_conversations')
            .select('id', { count: 'exact', head: true })
            .eq('student_id', userId),
          // Not sayısı
          supabase
            .from('student_notes')
            .select('id', { count: 'exact', head: true })
            .eq('student_id', userId),
          // Kurs ilerlemesi
          supabase
            .from('student_course_progress')
            .select('course_id, progress_percent, status, updated_at, courses(title, category, lessons(id))')
            .eq('student_id', userId)
            .order('updated_at', { ascending: false }),
          // Son 5 AI sohbeti
          supabase
            .from('ai_conversations')
            .select('id, title, created_at')
            .eq('student_id', userId)
            .order('created_at', { ascending: false })
            .limit(5),
          // Son 30 gün günlük aktivite
          supabase
            .from('student_daily_activity')
            .select('activity_date, lessons_completed, quizzes_completed')
            .eq('student_id', userId)
            .gte('activity_date', thirtyDaysAgo.toISOString().slice(0, 10))
            .order('activity_date', { ascending: false }),
        ]);

        if (cancelled) return;

        // Kurs ilerleme verilerini dönüştür
        const courseProgress: CourseProgress[] = (courseProgressRes.data ?? []).map((row: any) => {
          const totalLessons = row.courses?.lessons?.length ?? 0;
          const progressPercent = row.progress_percent ?? 0;
          const completedLessons = Math.round((progressPercent * totalLessons) / 100);
          return {
            courseId: row.course_id,
            title: row.courses?.title ?? 'Bilinmeyen Kurs',
            category: row.courses?.category ?? '',
            progressPercent,
            completedLessons,
            totalLessons,
            status: row.status ?? 'in_progress',
            lastActivityAt: row.updated_at ?? null,
          };
        });

        // Günlük aktivite hesapla — ayrıca not ve AI verilerini de ekle
        const notesPerDay: Record<string, number> = {};
        const aiPerDay: Record<string, number> = {};

        // Notları günlere dağıt
        const notesDetailRes = await supabase
          .from('student_notes')
          .select('created_at')
          .eq('student_id', userId)
          .gte('created_at', thirtyDaysAgoStr);

        const aiDetailRes = await supabase
          .from('ai_conversations')
          .select('created_at')
          .eq('student_id', userId)
          .gte('created_at', thirtyDaysAgoStr);

        (notesDetailRes.data ?? []).forEach((r: any) => {
          const d = r.created_at?.slice(0, 10);
          if (d) notesPerDay[d] = (notesPerDay[d] ?? 0) + 1;
        });

        (aiDetailRes.data ?? []).forEach((r: any) => {
          const d = r.created_at?.slice(0, 10);
          if (d) aiPerDay[d] = (aiPerDay[d] ?? 0) + 1;
        });

        const activityMap: Record<string, ActivityDay> = {};
        (activityRes.data ?? []).forEach((row: any) => {
          const d = row.activity_date;
          activityMap[d] = {
            date: d,
            lessonCount: row.lessons_completed ?? 0,
            noteCount: notesPerDay[d] ?? 0,
            aiCount: aiPerDay[d] ?? 0,
          };
        });

        // notesPerDay ve aiPerDay'deki günleri de ekle (student_daily_activity'de olmayan günler için)
        const allDays = new Set([
          ...Object.keys(activityMap),
          ...Object.keys(notesPerDay),
          ...Object.keys(aiPerDay),
        ]);

        allDays.forEach(d => {
          if (!activityMap[d]) {
            activityMap[d] = {
              date: d,
              lessonCount: 0,
              noteCount: notesPerDay[d] ?? 0,
              aiCount: aiPerDay[d] ?? 0,
            };
          } else {
            activityMap[d].noteCount = notesPerDay[d] ?? 0;
            activityMap[d].aiCount = aiPerDay[d] ?? 0;
          }
        });

        const activeDays = Object.values(activityMap)
          .filter(d => d.lessonCount + d.noteCount + d.aiCount > 0)
          .sort((a, b) => b.date.localeCompare(a.date));

        const partialData = {
          totalLessonsCompleted: completionsRes.count ?? 0,
          totalQuizAttempts: quizRes.count ?? 0,
          totalAiConversations: aiRes.count ?? 0,
          totalNotes: notesCountRes.count ?? 0,
          currentStreak: profile?.current_streak_days ?? 0,
          xp: profile?.xp ?? 0,
          age: profile?.age ?? null,
          courseProgress,
          activeDays: activeDays.slice(0, 30),
          activeDaysCount: activeDays.length,
          recentAiTopics: (recentAiRes.data ?? []).map((r: any) => ({
            id: r.id,
            title: r.title ?? 'AI Mentor Sohbeti',
            createdAt: r.created_at,
          })),
        };

        const critique = generateCritique(partialData);

        setData({ ...partialData, critique });
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Rapor yüklenemedi.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [userId, profile?.xp, profile?.current_streak_days]);

  return { data, isLoading, error };
}
